<script setup lang="ts">
/**
 * The captain beside the ladder: a portrait that ages through the three faces
 * (`landing-content.ts` `captainAge`) as the rank rises, with the current rank's
 * badge, name and place on the ladder. Sticky: it stays under the top bar while
 * the rungs scroll past. Draws what it is given; the rank is the parent's.
 */
import { computed } from 'vue';

import { RANKS, rankNameKey } from '@shared/ranks';

import { useI18n } from '@/i18n';
import { portraitArt, rankArt } from '@/ui/panel/art-path';

import { type CaptainAge, PORTRAIT_CLASS, captainAge } from '../landing-content';

const props = defineProps<{ rankIndex: number }>();
const { t } = useI18n();

const AGES: readonly CaptainAge[] = ['junior', 'senior', 'flag'];
const rank = computed(() => RANKS[props.rankIndex]!);
const age = computed(() => captainAge(props.rankIndex));
</script>

<template>
  <aside class="captain k-panel teak">
    <div class="faces">
      <img
        v-for="a in AGES"
        :key="a"
        class="face"
        :class="{ on: a === age }"
        :src="portraitArt(`${PORTRAIT_CLASS}_${a}`)"
        :alt="a === age ? t('landing.ranks.portrait', { rank: t(rankNameKey(rank.id)) }) : ''"
        :aria-hidden="a === age ? undefined : 'true'"
        width="300"
        height="400"
        loading="lazy"
        decoding="async"
      />
    </div>
    <div class="plaque">
      <img class="badge" :src="rankArt(rank.id)" alt="" width="64" height="64" decoding="async" />
      <span class="k-title rank-name">{{ t(rankNameKey(rank.id)) }}</span>
      <span class="k-chip">{{ t('landing.ranks.counter', { n: rankIndex + 1, total: RANKS.length }) }}</span>
    </div>
      </aside>

</template>

<style scoped>
/* The captain stays in view while the ladder scrolls past, under the top bar. */
.captain {
  position: sticky;
  top: 70px;
  z-index: 3;
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.5));
}

.faces {
  position: relative;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 0 0 3px #0d141c;
}

.face {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 600ms var(--ease);
}

.face.on {
  opacity: 1;
}

.plaque {
  display: grid;
  justify-items: start;
  gap: 6px;
  min-width: 0;
}

.badge {
  width: 52px;
  height: 52px;
  border-radius: 22%;
  box-shadow: 0 0 0 3px #0d141c;
}

.rank-name {
  font-size: clamp(1.1rem, 0.9rem + 1vw, 1.7rem);
  text-transform: uppercase;
}

@media (max-height: 520px) {
  .captain {
    top: 66px;
  }

  .faces {
    max-height: 34dvh;
    justify-self: center;
  }
}

@media (min-width: 820px) {
  .captain {
    grid-template-columns: 1fr;
    top: 96px;
    padding: 20px;
  }

  .plaque {
    justify-items: center;
    text-align: center;
  }

  .badge {
    width: 72px;
    height: 72px;
  }
}
</style>
